import { Link } from "react-router-dom";
import { ArrowRight, Phone, CheckCircle, Shield, Clock, Award, Paintbrush } from "lucide-react";
import { Button } from "@/components/ui/button";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import ServiceQuoteForm from "@/components/ServiceQuoteForm";
import ServiceFAQ from "@/components/ServiceFAQ";

const faqs = [
  { q: "How long does interior painting take?", a: "Most single rooms take 1-2 days, including prep, priming, and two coats of paint. A full home interior typically takes 3-7 days depending on size and complexity." },
  { q: "Do I need to move my furniture?", a: "No — we handle moving furniture to the center of each room and cover everything with drop cloths to protect your belongings." },
  { q: "How many coats of paint do you apply?", a: "We apply one coat of high-quality primer and two coats of finish paint for optimal coverage, color accuracy, and long-lasting durability." },
  { q: "What type of paint do you use?", a: "We use premium, low-VOC paints from trusted brands like Benjamin Moore and Sherwin-Williams for a durable, beautiful, and safe finish." },
  { q: "Can you help me choose colors?", a: "Absolutely! We offer a free color consultation to help you find the perfect palette. We bring sample swatches and advise on trending and timeless color schemes." },
];

const BEFORE = "/assets/images/beforeafter/interior-before.webp";
const AFTER = "/assets/images/beforeafter/interior-after.webp";

const InteriorPaintingPage = () => (
  <div className="bg-white" data-testid="interior-painting-page">
    {/* Hero */}
    <section className="relative pt-28 pb-20 lg:pb-32 overflow-hidden">
      <div className="absolute inset-0">
        <img src="https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=1920&q=85" alt="Interior Painting" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0c2340]/95 via-[#0c2340]/80 to-[#0c2340]/40"></div>
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 lg:pt-20">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-2 mb-6">
            <Paintbrush className="w-4 h-4 text-[#f97316]" />
            <span className="text-white/90 text-sm font-medium">Interior Painting</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Transform Your <span className="text-[#f97316]">Interior Spaces</span>
          </h1>
          <p className="text-gray-300 text-lg leading-relaxed mb-8 max-w-xl">
            From living rooms to bedrooms, kitchens to hallways — our expert painters deliver flawless, lasting results that bring your vision to life.
          </p>
          <div className="flex flex-wrap items-center gap-4 mb-8">
            <Link to="/contact"><Button className="bg-[#f97316] hover:bg-[#ea580c] text-white font-bold px-8 py-6 rounded-lg text-base shadow-lg">Get Free Quote <ArrowRight className="w-5 h-5 ml-2" /></Button></Link>
            <a href="tel:2368681195"><Button variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 font-bold px-8 py-6 rounded-lg text-base"><Phone className="w-5 h-5 mr-2" /> (236)868-1195</Button></a>
          </div>
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-[#f97316]" /><span className="text-gray-300 text-sm">Licensed & Insured</span></div>
            <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#f97316]" /><span className="text-gray-300 text-sm">On-Time Guarantee</span></div>
            <div className="flex items-center gap-2"><Award className="w-4 h-4 text-[#f97316]" /><span className="text-gray-300 text-sm">Satisfaction Guaranteed</span></div>
          </div>
        </div>
      </div>
    </section>

    {/* What's Included */}
    <section className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-full px-4 py-2 mb-6">
              <span className="w-2 h-2 bg-[#f97316] rounded-full"></span>
              <span className="text-[#f97316] font-semibold text-sm">What's Included</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-extrabold text-[#1a1a2e] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Premium Interior <span className="text-[#f97316]">Painting Service</span>
            </h2>
            <p className="text-gray-500 text-lg leading-relaxed mb-8">
              Every interior paint job includes thorough preparation, premium materials, and a clean finish — we treat your home like our own.
            </p>
            <div className="space-y-3">
              {["Walls, ceilings, and trim painting", "Surface prep: patching, sanding, priming", "Premium low-VOC paints included", "Furniture moving & drop cloth protection", "Color consultation available", "Detailed cleanup — we leave it spotless"].map((item) => (
                <div key={item} className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
                  <CheckCircle className="w-5 h-5 text-[#f97316] flex-shrink-0" />
                  <span className="text-gray-700 font-medium text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-[#15365a] rounded-2xl p-3 shadow-xl">
            <BeforeAfterSlider beforeImage={BEFORE} afterImage={AFTER} className="rounded-xl overflow-hidden" />
            <p className="text-white font-bold text-sm text-center py-3">Interior Door Refinishing — Before & After</p>
          </div>
        </div>
      </div>
    </section>

    {/* Quote Form */}
    <section className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div className="lg:sticky lg:top-32">
            <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-full px-4 py-2 mb-6">
              <span className="w-2 h-2 bg-[#f97316] rounded-full"></span>
              <span className="text-[#f97316] font-semibold text-sm">Get Started</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-extrabold text-[#1a1a2e] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Ready for a <span className="text-[#f97316]">Fresh New Look?</span></h2>
            <p className="text-gray-500 text-lg leading-relaxed mb-8">Get a free color consultation and painting quote. We'll get back to you within 24 hours.</p>
            <div className="space-y-4">
              {["Free color consultation included", "Premium paints & materials", "100% satisfaction guaranteed"].map((item) => (
                <div key={item} className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
                  <CheckCircle className="w-5 h-5 text-[#f97316] flex-shrink-0" />
                  <span className="text-gray-700 font-medium text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>
          <ServiceQuoteForm serviceName="Interior Painting" />
        </div>
      </div>
    </section>

    <ServiceFAQ faqs={faqs} />

    {/* CTA */}
    <section className="relative py-24 overflow-hidden">
      <div className="absolute inset-0 bg-[#f97316]"><div className="absolute -top-32 -right-32 w-96 h-96 bg-white/10 rounded-full"></div><div className="absolute -bottom-24 -left-24 w-72 h-72 bg-white/5 rounded-full"></div></div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
        <h2 className="text-3xl lg:text-5xl font-extrabold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>Ready to Transform<br />Your Space?</h2>
        <p className="text-white/90 text-lg mb-10 max-w-xl mx-auto">Contact us today for a free consultation and quote on your interior painting project.</p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/contact"><Button className="bg-white text-[#f97316] hover:bg-gray-100 font-bold rounded-lg px-8 py-6 text-base shadow-xl">Get Free Quote <ArrowRight className="w-5 h-5 ml-2" /></Button></Link>
          <a href="tel:2368681195"><Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 font-bold rounded-lg px-8 py-6 text-base"><Phone className="w-5 h-5 mr-2" /> (236)868-1195</Button></a>
        </div>
      </div>
    </section>
  </div>
);

export default InteriorPaintingPage;
